import { onMounted, ref } from 'vue'
import { useConsentSelections } from '#root/lib/useConsentSelections'
import { createInjectionState } from '@vueuse/shared'
import { tracker } from '#root/lib/tracker'

const [useProvideConsentUI, useConsentUI] = createInjectionState(
  (options?: { onOpen?: () => void }) => {
    const { allowedAdvertising, allowedAnalytics, consent } =
      useConsentSelections()
    const consentAdvertising = ref(allowedAdvertising())
    const consentAnalytics = ref(allowedAnalytics())
    const consentNecessary = ref(true)

    // UI state
    const cookieManageSnackbar = ref(false)
    const cookieNoticeSnackbar = ref(false)

    function applyCookieSettings(accept?: boolean) {
      if (typeof accept === 'boolean') {
        consentAdvertising.value = accept
        consentAnalytics.value = accept
      }
      cookieManageSnackbar.value = false
      cookieNoticeSnackbar.value = false

      updateConsent()
    }

    function checkForConsent() {
      if (consent.value === null && !cookieNoticeSnackbar.value) {
        consentAdvertising.value = true
        consentAnalytics.value = true
        cookieNoticeSnackbar.value = true
        cookieManageSnackbar.value = false
      }
    }

    function openCookieSettings() {
      cookieManageSnackbar.value = true
      cookieNoticeSnackbar.value = false

      if (
        options &&
        typeof options === 'object' &&
        typeof options.onOpen === 'function'
      )
        options.onOpen()
    }

    function updateConsent() {
      const advertising = consentAdvertising.value
      const analytics = consentAnalytics.value
      const selections = []

      if (advertising) selections.push('advertising')
      if (analytics) selections.push('analytics')

      consent.value = selections.length ? selections.join(',') : 'reject'

      if (tracker.gtag) {
        tracker.gtag('consent', 'update', {
          ad_storage: advertising ? 'granted' : 'denied',
          ad_user_data: advertising ? 'granted' : 'denied',
          ad_personalization: advertising ? 'granted' : 'denied',
          analytics_storage: analytics ? 'granted' : 'denied',
          functionality_storage: 'granted',
          personalization_storage: 'granted',
          security_storage: 'granted'
        })
        tracker.gtag('set', 'ads_data_redaction', !advertising)
      }

      if (tracker.posthog) {
        tracker.posthog.set_config({
          persistence: analytics ? 'localStorage+cookie' : 'memory'
        })
      }
    }

    onMounted(async () => {
      if (consent.value !== null) {
        const selections = consent.value.split(',')
        consentAdvertising.value = selections.includes('advertising')
        consentAnalytics.value = selections.includes('analytics')
      }
    })

    return {
      applyCookieSettings,
      checkForConsent,
      consent,
      consentAdvertising,
      consentAnalytics,
      consentNecessary,
      cookieManageSnackbar,
      cookieNoticeSnackbar,
      openCookieSettings,
      updateConsent
    }
  }
)

function useConsentUIOrThrow() {
  const consentUI = useConsentUI()
  if (consentUI == null)
    throw new Error(
      'Please call `useProvideConsentUI` on the appropriate parent component'
    )
  return consentUI
}

export { useProvideConsentUI, useConsentUIOrThrow }
